<script setup lang="ts">
import {computed, inject, onMounted, ref, watch} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useStore} from 'vuex'
import {useContextStore} from '@/store/modules/context'
import AppModal from '@/components/shared/AppModal.vue'
import SportSelector from '@/components/contextlist/SportSelector.vue'
import {Sport, SportData, useAllSports} from "@/composables/useSports";
import {useCookie} from "@/composables/useCookie";
import {KEY_IS_MOBILE} from "@/keys/keys";

interface Props {
  isOpen: boolean
}

const isMobile = inject(KEY_IS_MOBILE)

function setCookie(name: string, value: string, days = 365) {
  setAppCookie(name, value, { maxAgeSeconds: days * 24 * 60 * 60 })
}

const props = defineProps<Props>()
const emit = defineEmits(['update:isOpen'])

const router = useRouter()
const route = useRoute()
const contextStore = useContextStore()

const { setCookie: setAppCookie  } = useCookie()

const localVisible = ref(false)
const selectedSportOverride = ref<string | null>(null)

const selectedSport = computed(() => {
  if (selectedSportOverride.value) return selectedSportOverride.value

  const contextName = route.params.context as string
  if (contextName) {
    const currentContext = contextStore.contextMap[contextName]
    if (currentContext && currentContext.sports.length === 1) {
      return currentContext.sports[0].toUpperCase()
    }
  }

  return localStorage.getItem('selectedSport') || ''
})

watch(
    () => props.isOpen,
    (val) => {
      localVisible.value = val
      if (val) {
        selectedSportOverride.value = null // Reset override when opening modal to show current context
      }
    },
    {immediate: true}
)
watch(localVisible, (val) => emit('update:isOpen', val))

const close = () => (localVisible.value = false)

const allSportOptions = ref<SportData[]>([]);
onMounted(async () => {
  allSportOptions.value = await useAllSports();
});

const availableSportOptions = computed<SportData[]>(() => {
  const isBetBuilder = route.path.startsWith('/bet/') || route.path.startsWith('/betpic/')
  if (!isBetBuilder) return allSportOptions.value

  const contextName = route.params.context as string
  if (!contextName) return []

  const currentContext = contextStore.contextMap[contextName]
  if (!currentContext) return []

  const brand = currentContext.brand

  const singleSportContexts = Object.entries(contextStore.contextMap)
      .filter(([name, info]) => info.brand === brand && info.sports.length === 1)

  const sports = singleSportContexts.map(([name, info]) => info.sports[0].toLowerCase())

  return allSportOptions.value.filter(opt => sports.includes(opt.value.toLowerCase()))
})

const store = useStore()

async function onSportSelected(sport: Sport) {
  // Clear betting state before navigation
  store.commit('betBuilderModule/resetBetBuilderStore')
  store.commit('composeModule/resetComposeStore')
  store.commit('oddsModule/resetOddsStore')
  
  selectedSport.value = sport
  localStorage.setItem('selectedSport', sport)

  const isBetBuilder = route.path.startsWith('/bet/') || route.path.startsWith('/betpic/')
  const routePrefix = route.path.startsWith('/betpic/') ? '/betpic/' : '/bet/'

  if (isBetBuilder) {
    const contextName = route.params.context as string
    const currentContext = contextStore.contextMap[contextName]

    if (currentContext?.brand) {
      setCookie(currentContext.brand, sport)
    }
  }

  if (route.path.includes('/odds')) {
    if (sport === 'SOCCER') await router.push('/odds/supersetSoccer')
    else if (sport === 'AMERICAN_FOOTBALL') await router.push('/odds/supersetAmericanFootball')
    else if (sport === 'HORSE_RACING') await router.push('/odds/supersetRacing')
  } else if (isBetBuilder) {
    const contextName = route.params.context as string
    const currentContext = contextStore.contextMap[contextName]
    if (!currentContext) return

    const brand = currentContext.brand

    const targetContext = Object.entries(contextStore.contextMap).find(
        ([, info]) =>
            info.brand === brand &&
            info.sports.length === 1 &&
            info.sports[0].toLowerCase() === sport.toLowerCase()
    )?.[0]

    if (targetContext) {
      await router.push(`${routePrefix}${targetContext}`)
    }
  }

  close()
}

</script>

<template>
  <AppModal
    :visible="localVisible"
    @update:visible="localVisible = $event"
    @close="close"
    title="Select a Sport"
    max-width="max-w-xl"
    height="max-h-[80vh]"
  >
    <div class="overflow-y-auto flex-1 space-y-4">
      <div tabindex="0">
        <SportSelector
            :selectedSport="selectedSport"
            :sportOptions="availableSportOptions"
            :isCompactMode="false"
            @update:selectedSport="onSportSelected"
        />
      </div>
    </div>
  </AppModal>
</template>